import { SelectionModel } from '@angular/cdk/collections';
import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatSort } from '@angular/material/sort';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from './menu-item.model';
import { TableColumn } from './table-column.model';
import { ThemePalette } from '@angular/material/core';

@Component({
  selector: 'ecu-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.css'],
})
export class TableComponent<T> implements OnInit, AfterViewInit {
  private _dataSource: MatTableDataSource<T>;

  @Input() isLoading = false;
  @Input() noPagination = false;
  @Input() noSelection = false;
  @Input() noActionMenu = false;
  @Input() noSearchBar = false;
  @Input() emptyMessage = '';

  @Input()
  set dataSource(dataSource: MatTableDataSource<T>) {
    this._dataSource = dataSource;
    this.onDataSourceUpdated();
  }

  get dataSource(): MatTableDataSource<T> {
    return this._dataSource;
  }

  @Input() displayedColumns: Array<TableColumn> = [];
  @Input() actionMenuItems: MenuItem[] = [];

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  columns: string[];
  selection = new SelectionModel<T>(true, []);

  constructor(public readonly translate: TranslateService) {}

  ngOnInit(): void {
    if (this.displayedColumns) {
      const columnDefs = this.displayedColumns.map(
        (column) => column.columnDef
      );
      this.columns = this.noSelection ? columnDefs : ['select', ...columnDefs];
      this.columns = this.noActionMenu
        ? this.columns
        : [...this.columns, 'action'];
    }
  }

  ngAfterViewInit() {
    this.onDataSourceUpdated();
  }

  private onDataSourceUpdated() {
    if (this.dataSource) {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;

      this.dataSource.filterPredicate = (data: T, filter: string) => {
        const dataStr = Object.values(data).join(' ').toLowerCase();
        return dataStr.includes(filter);
      };
    }
  }

  isAllSelected() {
    if (!this.dataSource || !this.dataSource.data) {
      return false;
    }
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.data.forEach((row) => this.selection.select(row));
  }

  checkboxLabel(row?: T): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row`;
  }

  filteredActionMenuItems(row: any) {
    return this.actionMenuItems.filter((item) =>
      item.isHidden ? !item.isHidden(row) : true
    );
  }

  actionMenuItemTextColor(color: ThemePalette) {
    switch (color) {
      case 'accent':
        return `text-warning`;
      case 'warn':
        return `text-danger`;
      default:
        return undefined;
    }
  }

  sortColumn(column: string, direction: 'asc' | 'desc') {
    this.sort.active = column;
    this.sort.direction = direction;
    this.sort.sortChange.emit();
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }
}
